import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@/assets/icon-close.svg?react';
import reportGenerateImage from '@/assets/Report_generate.png';
import { ReportPopupStyles } from './styled';

interface Props {
  open: boolean;
  onClose: () => void;
  onTryNewReport?: () => void;
  children: React.ReactElement;
}

const ReportPopup: React.FC<Props> = ({ open, onClose, onTryNewReport, children }) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        open &&
        popupRef.current &&
        containerRef.current &&
        !popupRef.current.contains(event.target as Node) &&
        !containerRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onClose]);

  useEffect(() => {
    if (open && containerRef.current && popupRef.current) {
      const container = containerRef.current;
      const popup = popupRef.current;
      const containerRect = container.getBoundingClientRect();
      const popupRect = popup.getBoundingClientRect();
      const popupWidth = popupRect.width || 179;
      const viewportPadding = 14;

      // Find the report box (table-container) and align popup's right edge with its right edge
      const tableContainer = document.querySelector('.table-container');
      let left = 0;

      if (tableContainer) {
        const tableRect = tableContainer.getBoundingClientRect();
        // Calculate position so popup's right edge aligns with table's right edge
        // The popup's right edge should be at: containerRect.left + left + popupWidth = tableRect.right
        // So: left = tableRect.right - containerRect.left - popupWidth
        const tableRight = tableRect.right;
        const containerLeft = containerRect.left;
        
        // Calculate desired position
        left = tableRight - containerLeft - popupWidth;

        // Verify the popup's right edge doesn't exceed table's right edge
        const calculatedPopupRight = containerLeft + left + popupWidth;
        if (calculatedPopupRight > tableRight) {
          // If it would overflow, position it so right edge is exactly at table's right edge
          left = tableRight - containerLeft - popupWidth;
        }

        // Ensure it doesn't go too far left (minimum at button edge)
        if (left < 0) {
          left = 0;
        }

        // Final check: ensure popup's right edge doesn't exceed table's right edge
        const finalPopupRight = containerLeft + left + popupWidth;
        if (finalPopupRight > tableRight) {
          // Force it to stay within table bounds
          left = tableRight - containerLeft - popupWidth - 1; // -1 for safety margin
        }

        // Ensure it doesn't overflow viewport on the right
        const viewportRightEdge = containerLeft + popupWidth + left;
        if (viewportRightEdge > window.innerWidth - viewportPadding) {
          left = window.innerWidth - containerRect.right - popupWidth - viewportPadding;
          if (left < 0) {
            left = 0;
          }
        }
      } else {
        // Fallback: position below button
        left = 0;
      }

      popup.style.left = `${left}px`;
      popup.style.top = `${containerRect.height + 8}px`;
    }
  }, [open]);

  const handleTryNewReport = () => {
    onTryNewReport?.();
    onClose();
  };

  return (
    <ReportPopupStyles>
      <div ref={containerRef} className="report-popup-container">
        {React.cloneElement(children, {
          onClick: (e: React.MouseEvent) => {
            if (children.props.onClick) {
              children.props.onClick(e);
            }
          },
        })}
        {open && (
          <div ref={popupRef} className="report-popup-card">
            <div className="report-popup-arrow" />
            <button className="report-popup-close-btn" onClick={onClose}>
              <CloseIcon style={{ width: '16px', height: '16px' }} />
            </button>

            <div className="report-popup-icon">
              <img src={reportGenerateImage} alt="Report generate" />
            </div>

            <h2 className="report-popup-title">{t('seeTheFullPictureOfYourReportPerformance')}</h2>

            <p className="report-popup-description">
              {t('createAToTrackAnalyzeAndSharePerformance')}
            </p>

            <button className="report-popup-action-btn" onClick={handleTryNewReport}>
              {t('tryNewReport')}
            </button>
          </div>
        )}
      </div>
    </ReportPopupStyles>
  );
};

export default ReportPopup;
